/**
 * Gallery thumbnails, replacing agent/internal/imaging.
 *
 * The agent rendered these in Go and cached them on disk under
 * `%LOCALAPPDATA%\CitariaApp\thumbs`, keyed on path, mtime, size and width. The
 * measurement behind that cache still applies — a real event folder holds
 * several hundred frames, and revisiting one went from 680ms per thumbnail to
 * 2-6ms — so the cache is kept, in IndexedDB, on the same key.
 *
 * What is gone is the reason the agent needed a wide decode bound: it was
 * sizing around a Drive mount streaming online-only files from the cloud. With
 * Drive for Desktop out of the picture the watched folder is ordinary local
 * disk, so the limit here is CPU and the concurrency is sized to it.
 *
 * `imageOrientation: 'from-image'` does what agent/internal/imaging/exif.go did
 * by hand: a portrait frame the camera tagged with EXIF 6 must appear upright in
 * the grid, or the operator cannot tell which slot it will match.
 */

import { canvasContext, decodeBitmap, makeCanvas, toBlob } from '@/core/canvas'
import { BLOBS, get, put } from './db'

/** Decoding several full-resolution frames at once is what stalls the grid. */
const CONCURRENCY = 4

const inFlight = new Map<string, Promise<string>>()
const urls = new Map<string, string>()

/**
 * Resolved URLs by PHOTO, alongside the by-content map above.
 *
 * The content key needs a `File` — name, mtime and size — so finding a
 * thumbnail through it meant opening the file first. Every tile that mounted
 * paid a filesystem round trip just to learn its thumbnail was already sitting
 * in `urls`: measured, 240 opens to redraw a 120-photo gallery that needed no
 * rendering at all. The photo's identifier is already in hand and stable for as
 * long as the watcher knows the file, so it answers the same question without
 * touching the disk.
 *
 * Staleness matches what the grid always did: a tile keeps the thumbnail it
 * first resolved, since the watcher never re-announces a file it has seen.
 */
const byPhoto = new Map<string, string>()

function photoKey(identifier: string, width: number): string {
  return `${identifier}:${width}`
}

let active = 0
const waiting: (() => void)[] = []

async function slot<T>(fn: () => Promise<T>): Promise<T> {
  if (active >= CONCURRENCY) await new Promise<void>((resolve) => waiting.push(resolve))
  active += 1
  try {
    return await fn()
  } finally {
    active -= 1
    waiting.shift()?.()
  }
}

function cacheKey(file: File, width: number): string {
  // The agent's key, minus the path it no longer has. Name, mtime and size
  // together still change whenever the bytes do.
  return `thumb:${file.name}:${file.lastModified}:${file.size}:${width}`
}

async function render(file: File, width: number): Promise<Blob> {
  const bitmap = await decodeBitmap(file, { resizeWidth: width, resizeQuality: 'medium' })

  try {
    const canvas = makeCanvas(bitmap.width, bitmap.height)
    canvasContext(canvas).drawImage(bitmap, 0, 0)
    // JPEG, not PNG: these are photographs and a 350px PNG of one is several
    // times the size for no visible gain in a 150px grid cell.
    return await toBlob(canvas, 'image/jpeg', 0.82)
  } finally {
    bitmap.close()
  }
}

/**
 * A thumbnail's object URL, rendering and caching it on first ask.
 *
 * The URL is kept for the life of the tab and handed back on repeat calls: the
 * grid re-renders constantly as the operator selects photos, and minting a new
 * blob URL each time would leak one per render.
 */
export async function thumbUrl(file: File, width = 400): Promise<string> {
  const key = cacheKey(file, width)

  const ready = urls.get(key)
  if (ready) return ready

  const pending = inFlight.get(key)
  if (pending) return pending

  const work = slot(async () => {
    let blob = await get<Blob>(BLOBS, key)
    if (!blob) {
      blob = await render(file, width)
      // A cache write that fails on quota must not fail the thumbnail.
      await put(BLOBS, blob, key).catch(() => undefined)
    }
    const url = URL.createObjectURL(blob)
    urls.set(key, url)
    return url
  }).finally(() => {
    inFlight.delete(key)
  })

  inFlight.set(key, work)
  return work
}

/** A photo's thumbnail URL if it has already been resolved, without any I/O. */
export function cachedThumb(identifier: string, width = 400): string | undefined {
  return byPhoto.get(photoKey(identifier, width))
}

/** `thumbUrl`, remembered against the photo so the next lookup is free. */
export async function thumbForPhoto(identifier: string, file: File, width = 400): Promise<string> {
  const url = await thumbUrl(file, width)
  byPhoto.set(photoKey(identifier, width), url)
  return url
}

/** Releases every minted URL. Called when the watched folder changes. */
export function releaseThumbs(): void {
  for (const url of urls.values()) URL.revokeObjectURL(url)
  urls.clear()
  // Same URLs, now revoked. Leaving these would hand a remounting tile a dead
  // blob URL and a broken image.
  byPhoto.clear()
}
